import { useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Send, Loader2, MailCheck } from "lucide-react";

// Force Verification Send — bypasses the preview email gate so admins can
// smoke-check Resend rendering from staging without a production deploy.
// Kept intentionally tiny: one recipient, one template picker, one button.
export default function TestEmailCard() {
  const [recipient, setRecipient] = useState("");
  const [template, setTemplate] = useState("ping");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/test-send",
        { recipient: recipient || null, template });
      setLast({ id: data.email_id, to: data.recipient, at: new Date().toISOString(), env: data.env });
      toast.success(`Test email queued to ${data.recipient}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="test-email-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <MailCheck size={18} /> Force verification send
          <Badge variant="secondary" className="font-sans font-normal">bypasses preview gate</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Preview normally skips real Resend delivery. Use this to actually ship one email so you can smoke-check subject, sender name, colour, and layout before deploying. Defaults to the workspace's <code>DIGEST_RECIPIENT</code>.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Recipient (optional)</label>
            <Input type="email" placeholder="fallback: DIGEST_RECIPIENT"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              data-testid="test-email-recipient" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Template</label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="block w-40 h-10 rounded-md border bg-background px-3 text-sm"
              data-testid="test-email-template"
            >
              <option value="ping">Ping (bare)</option>
              <option value="digest_sample">Digest sample</option>
            </select>
          </div>
          <Button size="sm" disabled={busy} onClick={send} data-testid="test-email-send">
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
            Send test email
          </Button>
        </div>
        {last && (
          <div className="text-xs text-muted-foreground pt-1" data-testid="test-email-last">
            Last send: <span className="font-semibold text-foreground">{last.to}</span> · id <code>{last.id}</code> · env {last.env} · {new Date(last.at).toLocaleTimeString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
